import { useState, useRef, useEffect } from "react";
import { IoIosClose } from "react-icons/io";
import html2canvas from "html2canvas";
import { RiDraggable } from "react-icons/ri";
import { Bug, Camera } from "lucide-react";

import { createTicket } from "../../api/tickets";
import { getUserFirstLast } from "../../api/auth";
import { Input } from "../Universal/Input";
import { Button } from "../Universal/Button";
import { usePanels } from "../contexts/PanelsContext";
import { useRefs } from "../contexts/RefsContext";
import { useLayout } from "../contexts/LayoutContext";

const PopUpTicketsWindow = () => {
  const { isTicketsOpen, setIsTicketsOpen } = usePanels();
  const { captureRef } = useRefs();
  const { currentAppPage } = useLayout();

  const overlayRef = useRef(null);
  const dropdownRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [initialMousePos, setInitialMousePos] = useState({ x: 0, y: 0 });
  const [dimensions, setDimensions] = useState(
    () =>
      JSON.parse(localStorage.getItem("ticketWindowDimensions")) || {
        width: 480,
        height: 520,
      },
  );

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [username, setUsername] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isScreenshotEnabled, setIsScreenshotEnabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("ticketWindowPosition"));
    if (saved && typeof saved.x === "number" && typeof saved.y === "number") {
      setPosition(saved);
    }
  }, []);

  useEffect(() => {
    getUserFirstLast().then((response) => {
      setUsername(response.data.first_name + " " + response.data.last_name);
      setUserEmail(response.data.email);
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsTicketsOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [setIsTicketsOpen]);

  const toggleDropdown = () => {
    setIsTicketsOpen(!isTicketsOpen);
  };

  const handleMouseDown = (e) => {
    if (e.target.closest(".drag-handle")) {
      setIsDragging(true);
      setInitialMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const dx = e.clientX - initialMousePos.x;
    const dy = e.clientY - initialMousePos.y;
    const newX = position.x + dx;
    const newY = position.y + dy;

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const elementWidth = dropdownRef.current?.offsetWidth ?? 0;
    const elementHeight = dropdownRef.current?.offsetHeight ?? 0;

    const newPosition = {
      x: Math.max(0, Math.min(newX, windowWidth - elementWidth)),
      y: Math.max(0, Math.min(newY, windowHeight - elementHeight)),
    };

    setPosition(newPosition);
    setInitialMousePos({ x: e.clientX, y: e.clientY });
    localStorage.setItem("ticketWindowPosition", JSON.stringify(newPosition));
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleResize = () => {
    if (!dropdownRef.current) return;
    const newDimensions = {
      width: dropdownRef.current.offsetWidth,
      height: dropdownRef.current.offsetHeight,
    };
    setDimensions(newDimensions);
    localStorage.setItem(
      "ticketWindowDimensions",
      JSON.stringify(newDimensions),
    );
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleResize);
    if (dropdownRef.current) resizeObserver.observe(dropdownRef.current);
    return () => {
      if (dropdownRef.current) resizeObserver.unobserve(dropdownRef.current);
    };
  }, [dropdownRef.current]);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const submitTicket = async () => {
    if (isSubmitting) return;
    if (!title.trim()) {
      alert("Please provide a ticket title");
      return;
    }
    if (!description.trim()) {
      alert("Please provide a description");
      return;
    }

    setIsSubmitting(true);

    const pageLocation = currentAppPage;
    let screenshotMedia = "";

    if (isScreenshotEnabled) {
      const el = captureRef?.current;
      const overlayEl = overlayRef.current;

      if (!el || !overlayEl) {
        console.error("missing element(s) for screenshot!");
        setIsSubmitting(false);
        return;
      }

      overlayEl.style.visibility = "hidden";
      const canvas = await html2canvas(el);
      screenshotMedia = canvas.toDataURL("image/png");
      overlayEl.style.visibility = "";
    }

    await createTicket(
      { title, description, steps, username, userEmail, pageLocation },
      screenshotMedia,
    );

    setIsSubmitting(false);
    setIsTicketsOpen(false);
    setTitle("");
    setDescription("");
    setSteps("");
    setIsScreenshotEnabled(false);
  };

  if (!isTicketsOpen) return null;

  return (
    <div className="relative inline-block text-left" ref={overlayRef}>
      <div
        ref={dropdownRef}
        onMouseDown={handleMouseDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ticket-window-title"
        style={{
          left: position.x,
          top: position.y,
          width: `${dimensions.width}px`,
        }}
        className="fixed z-50 flex flex-col overflow-hidden rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md"
      >
        <div className="drag-handle cursor-grab active:cursor-grabbing flex items-center justify-between gap-2 px-4 py-3 border-b border-dropdownDivider bg-surface/80">
          <div className="flex items-center gap-2">
            <Bug strokeWidth={1.5} className="h-5 w-5 text-velBlue" />
            <p
              id="ticket-window-title"
              className="text-sm font-light text-mainText"
            >
              Submit Bug Report
            </p>
          </div>
          <div className="flex items-center gap-1">
            <RiDraggable className="h-5 w-5 text-mutedText/60 rotate-90" />
            <button
              onClick={toggleDropdown}
              aria-label="Close ticket window"
              className="p-1 rounded-lg transition-all duration-200 hover:bg-btnHover focus:outline-none focus:ring-2 focus:ring-inputFocus/30 cursor-pointer"
            >
              <IoIosClose className="h-6 w-6 text-mainText" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-4">
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brief summary of the issue"
          />

          <Input
            label="Description"
            type="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What happened? What did you expect?"
          />

          <Input
            label="Steps to Reproduce"
            help="Optional"
            type="textarea"
            rows={3}
            value={steps}
            onChange={(e) => setSteps(e.target.value)}
            placeholder={"1. Navigate to...\n2. Click on...\n3. Observe..."}
          />

          <div className="flex items-center justify-between px-4 py-3 rounded-lg border border-inputBorder bg-transparent transition-all duration-200">
            <div className="flex items-center gap-2">
              <Camera className="h-4 w-4 text-mutedText" />
              <p className="text-sm font-light text-mainText">
                Attach Screenshot
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isScreenshotEnabled}
              onClick={() => setIsScreenshotEnabled(!isScreenshotEnabled)}
              className={[
                "relative inline-flex items-center h-6 w-11 rounded-full transition-colors duration-200 cursor-pointer",
                "focus:outline-none focus:ring-2 focus:ring-inputFocus/60",
                isScreenshotEnabled ? "bg-velBlue" : "bg-mutedText/30",
              ].join(" ")}
            >
              <span
                className={[
                  "inline-block h-5 w-5 transform rounded-full bg-white shadow-sm",
                  "transition-transform duration-200",
                  isScreenshotEnabled ? "translate-x-5" : "translate-x-0.5",
                ].join(" ")}
              />
            </button>
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outlined" onClick={toggleDropdown} fullWidth>
              Cancel
            </Button>
            <Button onClick={submitTicket} fullWidth disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </div>

        <div className="px-4 py-2 border-t border-dropdownDivider bg-surface/80">
          <p className="text-xs font-light text-center text-mutedText/60">
            Drag header to move window
          </p>
        </div>
      </div>
    </div>
  );
};

export default PopUpTicketsWindow;
